import React from 'react';
import { Routes, Route, Link, Outlet, useLocation, Navigate, useNavigate } from 'react-router-dom';
import './App.css';
import AffordabilityCalculator from './components/AffordabilityCalculator';
import ResultsDisplay from './components/ResultsDisplay';
import PrivacyPolicy from './components/PrivacyPolicy';
import TermsAndConditions from './components/TermsAndConditions';
import CookieConsent from './components/CookieConsent';
import SEO from './components/SEO';
import Features from './components/Features';
import LoanComparison from './components/LoanComparison';
import SavingsGoals from './components/SavingsGoals';
import Support from './components/Support';
import AppStoreBadge from './components/AppStoreBadge';
import { useUTMTracking } from './hooks/useUTMTracking';
import Icon from './components/Icon';
import AppStoreRedirect from './components/AppStoreRedirect';
import AdminPortal from './admin/AdminPortal';
import { SpeedInsights } from '@vercel/speed-insights/react';

const ThemeContext = React.createContext({
  theme: 'light',
  toggleTheme: () => {}
});

const THEME_STORAGE_KEY = 'lown:theme';

const getSystemTheme = () => {
  if (typeof window === 'undefined') {
    return 'light';
  }

  return window.matchMedia('(prefers-color-scheme: dark)').matches ? 'dark' : 'light';
};

const getStoredTheme = () => {
  if (typeof window === 'undefined') {
    return null;
  }

  const storedPreference = window.localStorage.getItem(THEME_STORAGE_KEY);
  return storedPreference === 'light' || storedPreference === 'dark' ? storedPreference : null;
};

function useThemeMode() {
  const [userPreference, setUserPreference] = React.useState(() => getStoredTheme());
  const [systemPreference, setSystemPreference] = React.useState(() => getSystemTheme());

  const theme = userPreference ?? systemPreference;

  React.useEffect(() => {
    if (typeof window === 'undefined') {
      return undefined;
    }

    const mediaQuery = window.matchMedia('(prefers-color-scheme: dark)');
    const listener = (event) => {
      setSystemPreference(event.matches ? 'dark' : 'light');
    };

    if (mediaQuery.addEventListener) {
      mediaQuery.addEventListener('change', listener);
    } else {
      mediaQuery.addListener(listener);
    }

    return () => {
      if (mediaQuery.removeEventListener) {
        mediaQuery.removeEventListener('change', listener);
      } else {
        mediaQuery.removeListener(listener);
      }
    };
  }, []);

  React.useEffect(() => {
    if (typeof document === 'undefined') {
      return;
    }

    const root = document.documentElement;
    const body = document.body;
    root.setAttribute('data-theme', theme);
    body.setAttribute('data-theme', theme);
  }, [theme]);

  React.useEffect(() => {
    if (typeof window === 'undefined') {
      return;
    }

    if (userPreference) {
      window.localStorage.setItem(THEME_STORAGE_KEY, userPreference);
    } else {
      window.localStorage.removeItem(THEME_STORAGE_KEY);
    }
  }, [userPreference]);

  const toggleTheme = React.useCallback(() => {
    setUserPreference((prev) => {
      const nextTheme = (prev ?? theme) === 'dark' ? 'light' : 'dark';
      return nextTheme;
    });
  }, [theme]);

  return React.useMemo(
    () => ({
      theme,
      toggleTheme
    }),
    [theme, toggleTheme]
  );
}

// Shared Header Component
function Header() {
  const { theme, toggleTheme } = React.useContext(ThemeContext);

  return (
    <nav className="navbar">
      <div className="logo">
        <Link to="/">
          <img src="/Lown_logo_final_light.svg" alt="Lown Logo" className="logo-img" />
        </Link>
      </div>
      <div className="nav-links">
        {/* <Link to="/features">Features</Link> */}
        <Link to="/calculator">Calculator</Link>
        <Link to="/support">Support</Link>
        <button
          type="button"
          className="theme-toggle"
          onClick={toggleTheme}
          aria-label={`Activate ${theme === 'dark' ? 'light' : 'dark'} mode`}
          aria-pressed={theme === 'dark'}
        >
          <Icon
            name={theme === 'dark' ? 'moon' : 'sun'}
            size={20}
            className="theme-toggle__icon"
          />
        </button>
      </div>
    </nav>
  );
}

// Shared Footer Component
function Footer() {
  return (
    <footer className="footer">
      <div className="footer-content">
        <div className="footer-section">
          <h4>Lown</h4>
          <p>Built for first-time homebuyers • Educational purposes only</p>
        </div>
        <div className="footer-section">
        <h4>Contact</h4>
          <p>
          <a
            href="mailto:support@lown.app"
            style={{ color: "inherit", textDecoration: "underline" }}
          >
            support@lown.app
          </a>
          </p>
          <AppStoreBadge
            className="footer-app-store-cta"
            ariaLabel="Open the Lown App Store page"
          />
        </div>
      </div>
      <div className="footer-bottom">
        <p>&copy; 2025 Lown. All rights reserved.</p>
        <div className="footer-links">
          <Link to="/terms">Terms &amp; Conditions</Link>
          <Link to="/privacy">Privacy Policy</Link>
        </div>
      </div>
    </footer>
  );
}

// Layout Component to wrap pages
function Layout() {
  return (
    <>
      <Header />
      <div className="main-content-wrapper">
        <Breadcrumbs />
        <main>
          <Outlet /> {/* Child routes will render here */}
        </main>
      </div>
      <Footer />
    </>
  );
}

// Breadcrumbs Component
function Breadcrumbs() {
  const location = useLocation();
  const pathnames = location.pathname.split('/').filter((x) => x);

  // Don't show breadcrumbs on the homepage
  if (pathnames.length === 0) {
    return null;
  }

  return (
    <div className="breadcrumbs-container">
      <Link to="/">Home</Link>
      {pathnames.map((value, index) => {
        const to = `/${pathnames.slice(0, index + 1).join('/')}`;
        const isLast = index === pathnames.length - 1;
        const displayName = value.charAt(0).toUpperCase() + value.slice(1);

        return (
          <React.Fragment key={to}>
            <span className="breadcrumb-separator"> / </span>
            {isLast ? (
              <span>{displayName}</span>
            ) : (
              <Link to={to}>{displayName}</Link>
            )}
          </React.Fragment>
        );
      })}
    </div>
  );
}

// Page-specific content for the Homepage
function HomePage() {
  const homepageStructuredData = [
    {
      "@context": "https://schema.org",
      "@type": "WebPage",
      "name": "Lown Mortgage Calculator",
      "url": "https://www.lown.app/",
      "description": "Plan smarter with free, ad-free mortgage calculators that keep your savings, loan insights, and neighborhood research on-device.",
      "inLanguage": "en-US",
      "isPartOf": {
        "@type": "WebSite",
        "name": "Lown",
        "url": "https://www.lown.app/"
      },
      "about": [
        { "@type": "Thing", "name": "Mortgage calculator" },
        { "@type": "Thing", "name": "How much house can I afford" },
        { "@type": "Thing", "name": "Best mortgage lenders" },
        { "@type": "Thing", "name": "Houses for sale near me" },
        { "@type": "Thing", "name": "Eco-friendly homes" }
      ],
      "primaryImageOfPage": {
        "@type": "ImageObject",
        "url": "https://www.lown.app/lown_homescreen.png",
        "caption": "Screenshot of the Lown mortgage calculator app"
      },
      "potentialAction": [
        {
          "@type": "Action",
          "name": "Calculate mortgage affordability",
          "target": "https://www.lown.app/calculator"
        },
        {
          "@type": "Action",
          "name": "Compare mortgage lenders",
          "target": "https://www.lown.app/loan-comparison"
        },
        {
          "@type": "Action",
          "name": "Plan eco-friendly home goals",
          "target": "https://www.lown.app/savings-goals"
        }
      ],
      "breadcrumb": {
        "@type": "BreadcrumbList",
        "itemListElement": [
          {
            "@type": "ListItem",
            "position": 1,
            "name": "Home",
            "item": "https://www.lown.app/"
          }
        ]
      }
    },
    {
      "@context": "https://schema.org",
      "@type": "MobileApplication",
      "name": "Lown: Mortgage Calculator & Savings Tracker",
      "operatingSystem": "iOS",
      "applicationCategory": "FinanceApplication",
      "url": "https://www.lown.app/",
      "downloadUrl": "https://apps.apple.com/us/app/lown-loan-to-own/id6751429599",
      "offers": {
        "@type": "Offer",
        "price": "0",
        "priceCurrency": "USD"
      },
      "featureList": [
        "Track savings and progress toward down payment goals",
        "Manage multiple mortgages with principal and interest insights",
        "Explore how extra payments or rate buy-downs change payoff timelines",
        "Get personalized guidance on monthly income, debt, and cash flow",
        "Set financial goals with intuitive, ad-free tools built for homebuyers"
      ],
      "screenshot": "https://www.lown.app/lown_homescreen.png",
      "isAccessibleForFree": true,
      "keywords": [
        "mortgage calculator app",
        "how much house can I afford app",
        "compare mortgage rates",
        "FHA down payment assistance",
        "USDA loan eligibility",
        "VA loan requirements"
      ]
    }
  ];

  return (
    <>
      <SEO 
        title="Mortgage Calculator: How Much House Can I Afford & Best Mortgage Lenders"
        description="Plan smarter with Lown's free, ad-free mortgage calculator and savings tracker. Manage multiple mortgages, see how much house you can afford, compare top lenders, and keep every insight on your device."
        keywords="mortgage calculator, houses for sale near me, how much house can I afford, best mortgage lenders, mortgage savings tracker, mortgage refinance calculator, homes with home office space, eco-friendly homes near me, walkable neighborhoods, FHA down payment assistance, USDA loan eligibility, VA loan requirements, refinance calculator, budget tracker, loan payoff planner"
        url="https://www.lown.app/"
        image="https://www.lown.app/lown_homescreen.png"
        structuredData={homepageStructuredData}
      />
      
      {/* Hero Section */}
      <section className="hero">
        <div className="hero-content">
          <div className="hero-text animate-fade-in-up">
            <h1>Mortgage Calculator & Homeownership Toolkit for Modern Buyers</h1>
            <p className="hero-subtitle">Plan smarter with free, ad-free calculators that show how much house you can afford, compare the best mortgage lenders, surface eco-friendly houses for sale near me in walkable neighborhoods with dedicated office space, and track every mortgage payment privately on your device.</p>
            <div className="hero-cta">
              <AppStoreBadge />
            </div>
          </div>
          <div className="app-preview animate-fade-in-up">
            <div className="app-mockup">
              <img src="/lown_homescreen.png" alt="Lown App Preview" className="app-screenshot" />
            </div>
          </div>
        </div>
      </section>


      {/* Tools & Features Section */}
      <section id="tools" className="tools-section">
        <h2>Powerful Mortgage Tools for Your Journey</h2>
        <div className="tools-container">
          <div className="tool-card primary animate-fade-in-up">
            <div className="tool-icon app-icon">
              <Icon name="home" />
            </div>
            <h3>Home Affordability Calculator</h3>
            <p>Calculate exactly how much home you can afford based on income, debt, credit score, and down payment</p>
            <Link to="/calculator" className="tool-cta">Calculate Now</Link>
          </div>
          <div className="tool-card animate-fade-in-up">
            <div className="tool-icon app-icon">
              <Icon name="scale" />
            </div>
            <h3>Loan Comparison Tool</h3>
            <p>Compare multiple mortgage offers side by side to find the best rates, terms, and total costs</p>
            <Link to="/loan-comparison" className="tool-cta secondary">Learn More</Link>
          </div>
          <div className="tool-card animate-fade-in-up">
            <div className="tool-icon app-icon">
              <Icon name="target" />
            </div>
            <h3>Savings Goal Tracker</h3>
            <p>Gamify your savings with goal setting, progress tracking, and achievement rewards</p>
            <Link to="/savings-goals" className="tool-cta secondary">Learn More</Link>
          </div>
          <div className="tool-card animate-fade-in-up">
            <div className="tool-icon app-icon">
              <Icon name="chart" />
            </div>
            <h3>Amortization Tracker</h3>
            <p>Track real payments and get accurate amortization schedules with extra payment forecasting</p>
            <span className="coming-soon">Coming Soon</span>
          </div>
          <div className="tool-card animate-fade-in-up">
            <div className="tool-icon app-icon">
              <Icon name="bolt" />
            </div>
            <h3>Mortgage Payoff Calculator</h3>
            <p>Model the impact of additional payments on your loan term and total interest savings</p>
            <span className="coming-soon">Coming Soon</span>
          </div>
          <div className="tool-card animate-fade-in-up">
            <div className="tool-icon app-icon">
              <Icon name="clipboard" />
            </div>
            <h3>Pre-Approval Estimator</h3>
            <p>Get an estimate of your potential pre-approval amount based on your financial profile</p>
            <span className="coming-soon">Coming Soon</span>
          </div>
        </div>
        <div className="tools-cta">
          <Link to="/features" className="cta cta--secondary">
            <span className="cta__label">View All Features</span>
          </Link>
        </div>
      </section>

      {/* Educational Resources Section */}
      <section className="education-section">
        <h2>Expert Guidance for Smart Homebuying</h2>
        <div className="education-container">
          <div className="education-card animate-fade-in-up">
            <div className="education-icon app-icon">
              <Icon name="book" />
            </div>
            <h3>Affordability Guides</h3>
            <p>Learn how to calculate what you can afford and understand your debt-to-income ratio</p>
          </div>
          <div className="education-card animate-fade-in-up">
            <div className="education-icon app-icon">
              <Icon name="lightbulb" />
            </div>
            <h3>Loan Education</h3>
            <p>Understand different loan types, terms, and how to compare mortgage offers</p>
          </div>
          <div className="education-card animate-fade-in-up">
            <div className="education-icon app-icon">
              <Icon name="chart" />
            </div>
            <h3>Payment Tracking Tips</h3>
            <p>Learn how to track your loan payments and optimize your amortization schedule</p>
          </div>
        </div>
      </section>

      {/* Trust & Testimonials Section */}
      <section id="trust" className="trust-section">
        <h2>Trusted by Homebuyers</h2>
          <div className="trust-container">
          <div className="trust-statements">
            <div className="trust-statement animate-fade-in-up">
              <div className="trust-icon app-icon">
                <Icon name="lock" />
              </div>
              <h3>Privacy First</h3>
              <p>Your data stays with you. No third-party aggregators or data selling.</p>
            </div>
            <div className="trust-statement animate-fade-in-up">
              <div className="trust-icon app-icon">
                <Icon name="home" />
              </div>
              <h3>Built by Homeowners</h3>
              <p>Created by people who understand the journey firsthand.</p>
            </div>
            <div className="trust-statement animate-fade-in-up">
              <div className="trust-icon app-icon">
                <Icon name="sparkle" />
              </div>
              <h3>Modern Experience</h3>
              <p>Beautiful, intuitive tools designed for today's homebuyers.</p>
            </div>
          </div>
          <div className="testimonial animate-fade-in-up">
            <div className="testimonial-content">
              <p>"Lown's affordability calculator helped me understand exactly what I could afford and gave me the confidence to start my homebuying journey."</p>
              <div className="testimonial-author">
                <strong>Sarah M.</strong>
                <span>First-time homebuyer</span>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="cta-section">
        <div className="cta-container animate-fade-in-up">
          <h2>Ready to Start Your Homebuying Journey?</h2>
          <p>Explore the affordability calculator to understand your budget, then continue in the app when you&rsquo;re ready.</p>
          <div className="cta-buttons">
            <Link to="/calculator" className="cta cta--primary">
              <span className="cta__label">Try the Calculator</span>
            </Link>
          </div>
        </div>
      </section>
    </>
  );
}

// Page-specific content for the Calculator Page
function CalculatorPage() {
  return (
    <>
      <SEO 
        title="Home Affordability Calculator App | How Much House Can I Afford?"
        description="Use Lown's affordability calculator to see how much home you can comfortably afford. Get clarity on monthly payments, debt-to-income, and budget."
        keywords="home affordability calculator app, how much house can I afford app, mortgage calculator app, debt to income ratio calculator, mortgage affordability calculator, first-time homebuyer calculator, mortgage budgeting app"
        url="https://www.lown.app/calculator"
        structuredData={{
          "@context": "https://schema.org",
          "@type": "WebApplication",
          "name": "Lown Home Affordability Calculator",
          "description": "Free home affordability calculator app for first-time homebuyers and homeowners",
          "url": "https://www.lown.app/calculator",
          "applicationCategory": "FinanceApplication",
          "offers": {
            "@type": "Offer",
            "price": "0",
            "priceCurrency": "USD"
          },
          "featureList": [
            "Income and debt analysis",
            "Credit score consideration",
            "Down payment calculations",
            "Monthly payment breakdown",
            "Debt-to-income ratio analysis"
          ]
        }}
      />
      <div className="calculator-page">
        <AffordabilityCalculator />
      </div>
    </>
  );
}

function ResultsPage() {
  const location = useLocation();
  const navigate = useNavigate();
  const results = location.state?.results;

  // If no results data, redirect to the calculator
  if (!results) {
    return <Navigate to="/calculator" replace />;
  }

  return (
    <>
      <SEO 
        title="Your Home Affordability Results | Mortgage Calculator Results"
        description="View your personalized home affordability results. See your maximum home price, monthly payment breakdown, and loan details from Lown's calculator."
        keywords="affordability results, home price calculator results, mortgage payment calculator results, loan details, mortgage affordability results, home buying budget results"
        url="https://www.lown.app/calculator/results"
      />
      <div className="calculator-page">
         <div className="page-header">
        {/* <h1>Your Affordability Results</h1> */}
      </div>
      <ResultsDisplay results={results} onHome={() => navigate('/')} />
    </div>
    </>
  );
}

function AdminHostRedirect() {
  const location = useLocation();
  const navigate = useNavigate();

  React.useEffect(() => {
    if (typeof window === 'undefined') {
      return;
    }

    const host = window.location.hostname?.toLowerCase() || '';
    if (host.startsWith('admin.') && !location.pathname.startsWith('/admin')) {
      navigate('/admin', { replace: true });
    }
  }, [location.pathname, navigate]);

  return null;
}

function App() {
  // Initialize UTM tracking
  useUTMTracking();
  const themeContextValue = useThemeMode();

  return (
    <ThemeContext.Provider value={themeContextValue}>
      <div className="App">
        <AdminHostRedirect />
        <Routes>
          <Route path="/admin/*" element={<AdminPortal />} />
          <Route path="/" element={<Layout />}>
            <Route index element={<HomePage />} />
            <Route path="calculator" element={<CalculatorPage />} />
            <Route path="calculator/results" element={<ResultsPage />} />
            <Route path="features" element={<Features />} />
            <Route path="loan-comparison" element={<LoanComparison />} />
            <Route path="savings-goals" element={<SavingsGoals />} />
            <Route path="terms" element={<TermsAndConditions />} />
            <Route path="privacy" element={<PrivacyPolicy />} />
            <Route path="support" element={<Support />} />
            <Route path="app-store" element={<AppStoreRedirect />} />
          </Route>
        </Routes>

        {/* Cookie Consent Banner */}
        <CookieConsent />
        <SpeedInsights />
      </div>
    </ThemeContext.Provider>
  );
}

export default App;
